import { CHILD_COUNT_MAX } from "@/lib/import-field-validation";
import { useEffect, useRef, useState } from "react";
import type React from "react";
import { useIntl } from "react-intl";
import {
  newCondition,
  type Composition,
  type ConditionByTypename,
  type LanguageLevel,
  type MatchCondition,
  type MatchConditionTypename,
  type Operator,
} from "../design-codec";
import { LANGUAGES } from "../languages";
import { useRequirementsReferenceData } from "../use-requirements-reference-data";
import { PlusButton } from "@/components/design/PlusButton";
import {
  CompositionEditor,
  FieldLabel,
  INPUT_CLASS,
  LookupSelect,
  NumberValueInput,
  OperatorSelect,
  RequirementControls,
  RowShell,
  SELECT_CLASS,
  SubsectionHeader,
  TextValueInput,
} from "./row-primitives";

type LanguageCondition = ConditionByTypename<"LanguageCondition">;
type CityCondition = ConditionByTypename<"CityCondition">;

const LANGUAGE_LEVELS: LanguageLevel[] = ["A1", "A2", "B1", "B2", "C1", "C2", "NATIVE"];

const LANGUAGE_LEVEL_KEY: Record<LanguageLevel, { id: string; defaultMessage: string }> = {
  A1: { id: "match.requirements.cefr.A1", defaultMessage: "A1 – Anfänger" },
  A2: { id: "match.requirements.cefr.A2", defaultMessage: "A2 – Grundlagen" },
  B1: { id: "match.requirements.cefr.B1", defaultMessage: "B1 – Mittelstufe" },
  B2: { id: "match.requirements.cefr.B2", defaultMessage: "B2 – Selbstständig" },
  C1: { id: "match.requirements.cefr.C1", defaultMessage: "C1 – Fortgeschritten" },
  C2: { id: "match.requirements.cefr.C2", defaultMessage: "C2 – Verhandlungssicher" },
  NATIVE: { id: "match.requirements.cefr.NATIVE", defaultMessage: "Muttersprache" },
};

export interface DetailFieldDef {
  typename: MatchConditionTypename;
  label: { id: string; defaultMessage: string };
  operators: Operator[];
  shape: "uuid" | "integer" | "string" | "language";
  /**
   * Bounds for a `shape: "integer"` field. Needed because genesis validates none of this and stores the
   * value in the match design JSONB, while spark's own codec requires an integer — so an out-of-range or
   * fractional value is one spark cannot read back. The upper bound must also stay within Java's `int`,
   * since genesis's MatchDesign records read these as `int`.
   */
  bounds?: { min: number; max: number };
  source?: "genders" | "maritalStatuses" | "countries" | "jobDepartments" | "jobLocations";
  // Legacy types we still render for round-tripping saved MatchDesigns but
  // do not expose as add chips. AnnualSalaryCondition: genesis evaluator is a
  // no-op (MatchResultGenerateController.java:470) and Person.annualSalary
  // has no editor in discovery, so the row could never affect a match.
  legacy?: true;
}

// Ordered by descending importance – the +chip row renders in this order
// and the user picks from the most-relevant first.
// Exported for the invariant test that pins the integer bounds; not intended as a public API.
export const DETAIL_FIELDS: DetailFieldDef[] = [
  { typename: "JobDepartmentCondition", label: { id: "search.fieldDepartment", defaultMessage: "Abteilung" }, operators: ["EQ", "NE"], shape: "uuid", source: "jobDepartments" },
  { typename: "JobLocationCondition", label: { id: "search.fieldLocation", defaultMessage: "Arbeitsstandort" }, operators: ["EQ", "NE"], shape: "uuid", source: "jobLocations" },
  { typename: "LanguageCondition", label: { id: "match.labelLanguage", defaultMessage: "Sprache" }, operators: ["GE", "LE", "EQ"], shape: "language" },
  { typename: "AgeCondition", label: { id: "search.fieldAge", defaultMessage: "Alter" }, operators: ["EQ", "NE", "GT", "GE", "LT", "LE"], shape: "integer", bounds: { min: 0, max: 120 } },
  { typename: "AnnualSalaryCondition", label: { id: "match.requirements.condition.annualSalary", defaultMessage: "Jahresgehalt" }, operators: ["EQ", "NE", "GT", "GE", "LT", "LE"], shape: "integer", bounds: { min: 0, max: 2_147_483_647 }, legacy: true },
  { typename: "GenderCondition", label: { id: "search.fieldGender", defaultMessage: "Geschlecht" }, operators: ["EQ", "NE"], shape: "uuid", source: "genders" },
  { typename: "MaritalCondition", label: { id: "search.fieldMaritalStatus", defaultMessage: "Familienstand" }, operators: ["EQ", "NE"], shape: "uuid", source: "maritalStatuses" },
  { typename: "CountryCondition", label: { id: "search.fieldCountry", defaultMessage: "Land" }, operators: ["EQ", "NE"], shape: "uuid", source: "countries" },
  { typename: "CitizenshipCondition", label: { id: "search.fieldCitizenship", defaultMessage: "Staatsangehörigkeit" }, operators: ["EQ", "NE"], shape: "uuid", source: "countries" },
  { typename: "CityCondition", label: { id: "search.fieldCity", defaultMessage: "Wohnort" }, operators: ["EQ", "NE"], shape: "string" },
  { typename: "ChildrenCountCondition", label: { id: "search.fieldChildren", defaultMessage: "Anzahl Kinder" }, operators: ["EQ", "NE", "GT", "GE", "LT", "LE"], shape: "integer", bounds: { min: 0, max: CHILD_COUNT_MAX } },
];

const FIELD_BY_TYPE: Record<string, DetailFieldDef> = Object.fromEntries(
  DETAIL_FIELDS.map((f) => [f.typename, f]),
);

// Die Condition-Typen, die dieser Editor rendert/hinzufuegt. Von ChallengeStep genutzt, um im
// Bewerbung-Modus die Detail-Conditions von den anderen zu trennen.
export const DETAIL_TYPES: ReadonlySet<MatchConditionTypename> = new Set(
  DETAIL_FIELDS.map((f) => f.typename),
);

// Das Dial deckt 1..100 km ab; groessere Werte gehen nur ueber das Eingabefeld darueber.
const RADIUS_DIAL_MAX_KM = 100;
const RADIUS_MAX_KM = 1000;
// Dial-Geometrie im 200er-viewBox: Mitte (100,100), Vollausschlag (100 km) bei 80px.
const DIAL_C = 100;
const DIAL_FULL_PX = 80;

function RadiusControl({
  value,
  onChange,
  readOnly,
  label,
}: {
  value: number | null | undefined;
  onChange: (next: number | null) => void;
  readOnly?: boolean;
  label: string;
}) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  // Der Griff folgt beim Ziehen der Zeigerrichtung; der Radius selbst ist richtungsunabhaengig.
  const [angle, setAngle] = useState(-Math.PI / 4);
  const wrapRef = useRef<HTMLDivElement>(null);
  const dialRef = useRef<SVGSVGElement>(null);
  const draggingRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    function onDocPointerDown(event: PointerEvent) {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onDocKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onDocPointerDown);
    document.addEventListener("keydown", onDocKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onDocPointerDown);
      document.removeEventListener("keydown", onDocKeyDown);
    };
  }, [open]);

  const dialKm = Math.min(value ?? 0, RADIUS_DIAL_MAX_KM);
  const dialPx = (dialKm / RADIUS_DIAL_MAX_KM) * DIAL_FULL_PX;
  const handleX = DIAL_C + dialPx * Math.cos(angle);
  const handleY = DIAL_C + dialPx * Math.sin(angle);

  const dialLabel = intl.formatMessage({
    id: "match.requirements.cityRadiusDial",
    defaultMessage: "Umkreis wählen, von der Mitte nach außen ziehen",
  });
  const dialHint = intl.formatMessage({
    id: "match.requirements.cityRadiusHint",
    defaultMessage: "Ziehen bis 100 km, größere Werte oben eintragen",
  });

  function applyDialPointer(event: React.PointerEvent<HTMLDivElement>) {
    const svg = dialRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 2 * DIAL_C - DIAL_C;
    const y = ((event.clientY - rect.top) / rect.height) * 2 * DIAL_C - DIAL_C;
    const distance = Math.hypot(x, y);
    // Nahe der Mitte keine Winkelspruenge.
    if (distance > 6) setAngle(Math.atan2(y, x));
    onChange(Math.max(1, Math.min(RADIUS_DIAL_MAX_KM,
      Math.round((distance / DIAL_FULL_PX) * RADIUS_DIAL_MAX_KM))));
  }

  function onDialKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (readOnly) return;
    const current = Math.max(1, dialKm);
    let next: number;
    switch (event.key) {
      case "ArrowUp":
      case "ArrowRight":
        next = Math.min(RADIUS_DIAL_MAX_KM, current + 1);
        break;
      case "ArrowDown":
      case "ArrowLeft":
        next = Math.max(1, current - 1);
        break;
      case "PageUp":
        next = Math.min(RADIUS_DIAL_MAX_KM, current + 10);
        break;
      case "PageDown":
        next = Math.max(1, current - 10);
        break;
      case "Home":
        next = 1;
        break;
      case "End":
        next = RADIUS_DIAL_MAX_KM;
        break;
      default:
        return;
    }
    event.preventDefault();
    onChange(next);
  }

  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        disabled={readOnly}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={label}
        title={label}
        className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md bg-ink/[0.06] px-2.5 text-ink/45 transition hover:bg-ink/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30 disabled:opacity-60"
      >
        <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4 shrink-0" fill="none">
          <circle cx="10" cy="10" r="2" fill="currentColor" />
          <circle cx="10" cy="10" r="5" stroke="currentColor" strokeWidth="1.4" opacity="0.7" />
          <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.1" opacity="0.35" />
        </svg>
        <span className="text-[12px] font-medium tabular-nums text-ink">{value ?? "–"}</span>
        <span className="text-[11px] text-ink/40">km</span>
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label={label}
          className="absolute left-0 top-9 z-[var(--z-dropdown)] w-60 rounded-md bg-surface p-3 shadow-lg ring-1 ring-ink/10"
        >
          <label className="flex items-center gap-2">
            <span className="min-w-0 flex-1 text-[11px] font-medium text-ink/70">{label}</span>
            <input
              type="number"
              min={1}
              max={RADIUS_MAX_KM}
              value={value ?? ""}
              disabled={readOnly}
              placeholder="50"
              onChange={(event) => {
                if (event.target.value === "") {
                  onChange(null);
                  return;
                }
                const next = Number(event.target.value);
                onChange(Number.isFinite(next) && next > 0
                  ? Math.min(RADIUS_MAX_KM, Math.round(next))
                  : null);
              }}
              className={`${INPUT_CLASS} w-20 shrink-0 text-right tabular-nums`}
            />
          </label>
          <div
            role="slider"
            tabIndex={readOnly ? -1 : 0}
            aria-label={dialLabel}
            aria-valuemin={1}
            aria-valuemax={RADIUS_DIAL_MAX_KM}
            aria-valuenow={Math.max(1, dialKm)}
            aria-valuetext={`${value ?? 0} km`}
            onKeyDown={onDialKeyDown}
            onPointerDown={(event) => {
              if (readOnly) return;
              event.currentTarget.setPointerCapture(event.pointerId);
              draggingRef.current = true;
              applyDialPointer(event);
            }}
            onPointerMove={(event) => {
              if (draggingRef.current) applyDialPointer(event);
            }}
            onPointerUp={() => {
              draggingRef.current = false;
            }}
            onPointerCancel={() => {
              draggingRef.current = false;
            }}
            className="mt-2 touch-none select-none rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
          >
            <svg ref={dialRef} aria-hidden="true" viewBox="0 0 200 200" className="mx-auto block h-44 w-44">
              {[20, 40, 60, 80].map((r) => (
                <circle key={r} cx={DIAL_C} cy={DIAL_C} r={r} fill="none" strokeWidth="1" className="stroke-ink/10" />
              ))}
              <circle
                cx={DIAL_C}
                cy={DIAL_C}
                r={Math.max(dialPx, 2)}
                strokeWidth="1.5"
                className="fill-accent/15 stroke-accent/60"
              />
              <line
                x1={DIAL_C}
                y1={DIAL_C}
                x2={handleX}
                y2={handleY}
                strokeWidth="1.5"
                strokeDasharray="3 3"
                className="stroke-accent/70"
              />
              <circle cx={DIAL_C} cy={DIAL_C} r={3} className="fill-accent" />
              {!readOnly ? (
                <circle
                  cx={handleX}
                  cy={handleY}
                  r={7}
                  strokeWidth="2"
                  className="cursor-grab fill-surface stroke-accent"
                />
              ) : null}
            </svg>
          </div>
          <p className="pt-1 text-center text-[10px] text-ink/45">{dialHint}</p>
        </div>
      ) : null}
    </div>
  );
}

export interface DetailsSectionProps {
  conditions: MatchCondition[];
  onChange: (next: MatchCondition[]) => void;
  readOnly?: boolean;
  // Muss/Kann-Schloss pro Zeile anzeigen + Muss-Kriterien nach oben sortieren. In allen Modi
  // moeglich (spark #151, @adr SPARK-0030-match-result-scoring-atom-derived@1) — entkoppelt von der Einzelperson-Semantik unten.
  showRequirement?: boolean;
  // Einzelperson-Bewertung (Bewerbung/Rank): Composition ist sinnlos (fix "= genau 1", kein
  // Composition-Editor, uuid/string ohne Operator-Selektor). In Team-Modi (Optimize/Assess)
  // aus, damit MIN/MAX/ALL editierbar bleiben.
  singlePerson?: boolean;
  // Titel weglassen, wenn der Wrapper (Disclosure) ihn schon traegt.
  hideHeader?: boolean;
}

export function DetailsSection({ conditions, onChange, readOnly, showRequirement, singlePerson, hideHeader }: DetailsSectionProps) {
  const ref = useRequirementsReferenceData();
  const intl = useIntl();
  const sectionTitle = intl.formatMessage({
    id: "match.requirements.personDetails",
    defaultMessage: "Personen-Details",
  });
  const languageAriaLabel = intl.formatMessage({ id: "match.labelLanguage", defaultMessage: "Sprache" });
  const levelAriaLabel = intl.formatMessage({ id: "match.requirements.aria.cefrLevel", defaultMessage: "Niveau" });
  const choosePlaceholder = intl.formatMessage({ id: "match.optChoose", defaultMessage: "– wählen –" });

  function patch(index: number, next: MatchCondition) {
    const updated = conditions.slice();
    updated[index] = next;
    onChange(updated);
  }
  function remove(index: number) {
    const updated = conditions.slice();
    updated.splice(index, 1);
    onChange(updated);
  }
  function addByTypename(typename: MatchConditionTypename) {
    const def = FIELD_BY_TYPE[typename];
    if (!def) return;
    // Bewerbung: Einzelperson -> Composition ist sinnlos, fix auf "genau 1" und kein Editor.
    const composition: Composition | undefined = singlePerson ? { operator: "EXACT", value: 1 } : undefined;
    if (def.shape === "language") {
      onChange([
        ...conditions,
        newCondition("LanguageCondition", {
          languageTag: "en",
          level: "B2",
          operator: "GE",
          ...(composition ? { composition } : {}),
        }),
      ]);
      return;
    }
    const base: Partial<Record<string, unknown>> = {};
    if (def.shape === "uuid") base.id = "";
    else if (def.shape === "integer") base.value = 0;
    else if (def.shape === "string") base.value = "";
    if (typename === "CityCondition") {
      base.radiusKm = 50;
      // Neue Wohnort-Conditions starten ohne KI; der Bewertungsstil-Schalter oben
      // schreibt den aiMode danach modusweit (spark #233).
      base.aiMode = "NO_AI";
    }
    if (composition) base.composition = composition;
    const created = newCondition(typename, base as never);
    onChange([...conditions, created]);
  }

  // Anzeige-Reihenfolge: Muss-Kriterien immer oben, Kann darunter (stabile Sortierung).
  // patch/remove arbeiten indexbasiert, daher Original-Index mitschleppen.
  const displayRows = conditions.map((c, i) => ({ c, i }));
  if (showRequirement) {
    displayRows.sort((a, b) => Number(b.c.requirement === "MUST") - Number(a.c.requirement === "MUST"));
  }

  return (
    <div>
      {hideHeader ? null : (
        <SubsectionHeader title={sectionTitle} />
      )}
      {/* Gleicher Zeilenrhythmus wie Benoetigte Berufserfahrung (space-y-2). */}
      <div className="space-y-2">
      {displayRows.map(({ c, i }) => {
        const def = FIELD_BY_TYPE[c.__typename];
        if (!def) return null;
        if (def.shape === "language") {
          const lc = c as LanguageCondition;
          return (
            <RowShell key={i} onRemove={() => remove(i)} readOnly={readOnly}>
              {/* Schloss vorne, konsistent zur Ergebnis-Darstellung (Status vorn). */}
              {showRequirement ? (
                <RequirementControls
                  requirement={lc.requirement}
                  onChange={(req) => patch(i, { ...lc, requirement: req })}
                  readOnly={readOnly}
                />
              ) : null}
              <FieldLabel>{intl.formatMessage(def.label)}</FieldLabel>
              <select
                className={SELECT_CLASS}
                value={lc.languageTag}
                disabled={readOnly}
                onChange={(e) => patch(i, { ...lc, languageTag: e.target.value })}
                aria-label={languageAriaLabel}
              >
                {LANGUAGES.map((l) => (
                  <option key={l.tag} value={l.tag}>
                    {l.labelDe}
                  </option>
                ))}
              </select>
              <OperatorSelect
                value={lc.operator}
                allowed={def.operators}
                readOnly={readOnly}
                onChange={(op) => patch(i, { ...lc, operator: op })}
              />
              <select
                className={SELECT_CLASS}
                value={lc.level}
                disabled={readOnly}
                onChange={(e) => patch(i, { ...lc, level: e.target.value as LanguageLevel })}
                aria-label={levelAriaLabel}
              >
                {LANGUAGE_LEVELS.map((lvl) => (
                  <option key={lvl} value={lvl}>
                    {intl.formatMessage(LANGUAGE_LEVEL_KEY[lvl])}
                  </option>
                ))}
              </select>
              {!singlePerson ? (
                <CompositionEditor
                  value={lc.composition}
                  readOnly={readOnly}
                  onChange={(comp) => patch(i, { ...lc, composition: comp })}
                />
              ) : null}
            </RowShell>
          );
        }
        const lookupOptions =
          def.source === "genders"
            ? ref.genders
            : def.source === "maritalStatuses"
              ? ref.maritalStatuses
              : def.source === "jobDepartments"
                ? ref.jobDepartments
                : def.source === "jobLocations"
                  ? ref.jobLocations
                  : ref.countries;
        return (
          <RowShell
            key={i}
            onRemove={() => remove(i)}
            readOnly={readOnly}
          >
            {/* Schloss vorne, konsistent zur Ergebnis-Darstellung (Status vorn). */}
            {showRequirement ? (
              <RequirementControls
                requirement={c.requirement}
                onChange={(req) => patch(i, { ...c, requirement: req } as MatchCondition)}
                readOnly={readOnly}
              />
            ) : null}
            <FieldLabel>{intl.formatMessage(def.label)}</FieldLabel>

            {def.shape === "uuid" ? (
              <LookupSelect
                value={(c as { id?: string }).id ?? ""}
                options={lookupOptions}
                placeholder={choosePlaceholder}
                readOnly={readOnly}
                onChange={(next) => patch(i, { ...c, id: next } as MatchCondition)}
              />
            ) : def.shape === "integer" ? (
              <NumberValueInput
                value={(c as { value: number }).value}
                onChange={(next) => patch(i, { ...c, value: next } as MatchCondition)}
                readOnly={readOnly}
                /* v8 ignore next 2 -- unreachable fallback: every shape:"integer" field declares
                   bounds, pinned by the DETAIL_FIELDS invariant test */
                min={def.bounds?.min ?? 0}
                max={def.bounds?.max}
              />
            ) : (
              <TextValueInput
                value={(c as { value: string }).value}
                onChange={(next) => patch(i, { ...c, value: next } as MatchCondition)}
                readOnly={readOnly}
              />
            )}

            {c.__typename === "CityCondition" ? (
              <RadiusControl
                value={(c as CityCondition).radiusKm}
                onChange={(next) => patch(i, { ...c, radiusKm: next } as MatchCondition)}
                readOnly={readOnly}
                label={intl.formatMessage({
                  id: "match.requirements.cityRadius",
                  defaultMessage: "Umkreis in Kilometern",
                })}
              />
            ) : null}

            {/* Bewerbung: reine "hat/ist"-Attribute (uuid/string) sind immer "= genau", kein
                Operator-Selektor. Integer (Alter/Kinder) behalten ihn fuer >=/<=/Bereiche. */}
            {!singlePerson || def.shape === "integer" ? (
              <OperatorSelect
                value={c.operator}
                allowed={def.operators}
                readOnly={readOnly}
                onChange={(op) => patch(i, { ...c, operator: op })}
              />
            ) : null}
            {!singlePerson ? (
              <CompositionEditor
                value={c.composition}
                readOnly={readOnly}
                onChange={(comp) => patch(i, { ...c, composition: comp })}
              />
            ) : null}
          </RowShell>
        );
      })}
      </div>
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-2">
          {DETAIL_FIELDS.filter((f) => !f.legacy).map((f) => (
            <PlusButton
              key={f.typename}
              label={intl.formatMessage(f.label)}
              onClick={() => addByTypename(f.typename)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
